// Utilitários para melhorar a experiência de autenticação
export const authStorage = {
  // Salvar dados temporários do usuário
  saveTemporaryData: (email: string, fullName?: string, lastAction: 'login' | 'register' = 'login') => {
    localStorage.setItem('masterclub_saved_email', email);
    if (fullName) {
      localStorage.setItem('masterclub_saved_name', fullName);
    }
    localStorage.setItem('masterclub_last_action', lastAction);
    localStorage.setItem('masterclub_last_action_time', Date.now().toString());
  },

  // Atualizar última ação (login/register)
  setLastAction: (action: 'login' | 'register') => {
    localStorage.setItem('masterclub_last_action', action);
    localStorage.setItem('masterclub_last_action_time', Date.now().toString());
  },

  // Recuperar dados salvos
  getSavedData: () => {
    return {
      email: localStorage.getItem('masterclub_saved_email') || '',
      fullName: localStorage.getItem('masterclub_saved_name') || '',
      lastAction: localStorage.getItem('masterclub_last_action') || 'login',
      lastActionTime: localStorage.getItem('masterclub_last_action_time') || '0'
    };
  },

  // Marcar que o usuário está aguardando verificação
  setPendingVerification: (email: string) => {
    localStorage.setItem('masterclub_pending_verification', 'true');
    localStorage.setItem('masterclub_pending_email', email);
    localStorage.setItem('masterclub_verification_time', Date.now().toString());
  },

  // Verificar se há verificação pendente
  hasPendingVerification: () => {
    const pending = localStorage.getItem('masterclub_pending_verification');
    const email = localStorage.getItem('masterclub_pending_email');
    const time = localStorage.getItem('masterclub_verification_time');
    
    if (pending === 'true' && email && time) {
      // Verificar se não passou muito tempo (24 horas)
      const timeDiff = Date.now() - parseInt(time);
      const hoursDiff = timeDiff / (1000 * 60 * 60);
      
      if (hoursDiff < 24) {
        return { email, timeDiff };
      } else {
        // Limpar dados expirados
        authStorage.clearPendingVerification();
      }
    }
    
    return null;
  },

  // Limpar verificação pendente
  clearPendingVerification: () => {
    localStorage.removeItem('masterclub_pending_verification');
    localStorage.removeItem('masterclub_pending_email');
    localStorage.removeItem('masterclub_verification_time');
  },

  // Limpar todos os dados salvos
  clearAll: () => {
    localStorage.removeItem('masterclub_saved_email');
    localStorage.removeItem('masterclub_saved_name');
    localStorage.removeItem('masterclub_last_action');
    localStorage.removeItem('masterclub_last_action_time');
    authStorage.clearPendingVerification();
  }
};

// Validador de email melhorado
export const validateEmail = (email: string): { isValid: boolean; message?: string } => {
  if (!email) {
    return { isValid: false, message: 'Email é obrigatório' };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { isValid: false, message: 'Formato de email inválido' };
  }

  // Verificar domínios suspeitos ou temporários
  const suspiciousDomains = [
    '10minutemail.com', 'tempmail.org', 'guerrillamail.com', 
    'mailinator.com', 'throwaway.email'
  ];
  
  const domain = email.toLowerCase().split('@')[1];
  if (suspiciousDomains.includes(domain)) {
    return { isValid: false, message: 'Por favor, use um email permanente' };
  }

  return { isValid: true };
};

// Validador de senha melhorado
export const validatePassword = (password: string): { isValid: boolean; message?: string; strength: 'weak' | 'medium' | 'strong' } => {
  if (!password) {
    return { isValid: false, message: 'Senha é obrigatória', strength: 'weak' };
  }

  if (password.length < 6) {
    return { isValid: false, message: 'Senha deve ter pelo menos 6 caracteres', strength: 'weak' };
  }

  if (password.length < 8) {
    return { isValid: true, message: 'Senha aceita, mas recomendamos 8+ caracteres', strength: 'medium' };
  }

  // Verificar força da senha
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  const strengthScore = [hasUpperCase, hasLowerCase, hasNumbers, hasSpecialChar].filter(Boolean).length;

  if (strengthScore >= 3 && password.length >= 8) {
    return { isValid: true, strength: 'strong' };
  } else if (strengthScore >= 2) {
    return { isValid: true, strength: 'medium' };
  } else {
    return { isValid: true, strength: 'weak' };
  }
};

// Utilitários para feedback visual
export const getPasswordStrengthColor = (strength: 'weak' | 'medium' | 'strong') => {
  switch (strength) {
    case 'weak': return 'text-red-400';
    case 'medium': return 'text-yellow-400';
    case 'strong': return 'text-green-400';
  }
};

export const getPasswordStrengthText = (strength: 'weak' | 'medium' | 'strong') => {
  switch (strength) {
    case 'weak': return 'Fraca';
    case 'medium': return 'Média';
    case 'strong': return 'Forte';
  }
};

// Detectar se o usuário está voltando após clicar no link de confirmação
export const detectReturnFromEmailConfirmation = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const hasAuthParams = urlParams.has('access_token') || urlParams.has('refresh_token') || urlParams.has('type');
  
  if (hasAuthParams) {
    const pending = authStorage.hasPendingVerification();
    if (pending) {
      authStorage.clearPendingVerification();
      return {
        isReturningFromConfirmation: true,
        email: pending.email
      };
    }
  }
  
  return { isReturningFromConfirmation: false };
};

// Mensagens personalizadas de erro
export const getAuthErrorMessage = (error: any): string => {
  if (!error?.message) return 'Ocorreu um erro inesperado';

  const message = error.message.toLowerCase();

  if (message.includes('invalid login credentials')) {
    return 'Email ou senha incorretos. Verifique suas credenciais.';
  }
  
  if (message.includes('email not confirmed')) {
    return 'Por favor, confirme seu email antes de fazer login. Verifique sua caixa de entrada.';
  }
  
  if (message.includes('user already registered')) {
    return 'Este email já está cadastrado. Tente fazer login ou recuperar sua senha.';
  }
  
  if (message.includes('password should be at least')) {
    return 'A senha deve ter pelo menos 6 caracteres.';
  }
  
  if (message.includes('invalid email')) {
    return 'Formato de email inválido.';
  }
  
  if (message.includes('rate limit')) {
    return 'Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.';
  }

  if (message.includes('email address not authorized')) {
    return 'Email não autorizado para cadastro. Entre em contato com o suporte.';
  }

  return error.message;
};